
/**
 * Utilitário de Criptografia Zero-Knowledge Profissional com Ancoragem de Ambiente
 */

const ITERATIONS = 600000;
const ALGO_KEY_DERIVATION = 'PBKDF2';
const ALGO_ENCRYPTION = 'AES-GCM';
const RECOVERY_STATIC_SALT = "UkVDT1ZFUllfU0FMVF9WMQ=="; 

const encode = (str: string) => new TextEncoder().encode(str);
const decode = (buf: ArrayBuffer) => new TextDecoder().decode(buf);

const bufferToBase64 = (buf: ArrayBuffer | Uint8Array) => {
  const bytes = buf instanceof Uint8Array ? buf : new Uint8Array(buf);
  return btoa(String.fromCharCode(...bytes));
};
const base64ToBuffer = (base64: string) => {
  try {
    const sanitized = base64.replace(/["\s]/g, '');
    const binary = atob(sanitized);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes.buffer;
  } catch (e) {
    throw new Error("Formato inválido.");
  }
};

/**
 * Gera uma assinatura única baseada no hardware e software do usuário.
 * Isso impede que a chave da sessão seja portátil.
 */
const getEnvironmentFingerprint = (): string => {
  const { userAgent, language, hardwareConcurrency } = navigator;
  const { width, height, colorDepth } = window.screen;
  return `${userAgent}|${language}|${hardwareConcurrency}|${width}x${height}|${colorDepth}`;
};

export const generateRandomMasterKey = async (): Promise<CryptoKey> => {
  return window.crypto.subtle.generateKey(
    { name: ALGO_ENCRYPTION, length: 256 },
    true,
    ['encrypt', 'decrypt']
  );
};

export const deriveKeyFromPassword = async (password: string, saltStr: string): Promise<CryptoKey> => {
  const salt = base64ToBuffer(saltStr);
  const keyMaterial = await window.crypto.subtle.importKey(
    'raw', encode(password), ALGO_KEY_DERIVATION, false, ['deriveKey']
  );

  return window.crypto.subtle.deriveKey(
    { name: ALGO_KEY_DERIVATION, salt, iterations: ITERATIONS, hash: 'SHA-256' },
    keyMaterial,
    { name: ALGO_ENCRYPTION, length: 256 },
    true,
    ['encrypt', 'decrypt']
  );
};

export const wrapKey = async (keyToWrap: CryptoKey, wrappingKey: CryptoKey): Promise<string> => {
  const iv = window.crypto.getRandomValues(new Uint8Array(12));
  const rawKey = await window.crypto.subtle.exportKey('raw', keyToWrap);
  const encrypted = await window.crypto.subtle.encrypt({ name: ALGO_ENCRYPTION, iv }, wrappingKey, rawKey);
  return `${bufferToBase64(iv)}.${bufferToBase64(encrypted)}`;
};

export const unwrapKey = async (wrappedKeyStr: string, wrappingKey: CryptoKey): Promise<CryptoKey | null> => {
  try {
    const sanitized = wrappedKeyStr.replace(/["\s]/g, '');
    const [ivBase64, ciphertextBase64] = sanitized.split('.');
    const decryptedRaw = await window.crypto.subtle.decrypt(
      { name: ALGO_ENCRYPTION, iv: base64ToBuffer(ivBase64) },
      wrappingKey,
      base64ToBuffer(ciphertextBase64)
    );
    return window.crypto.subtle.importKey('raw', decryptedRaw, ALGO_ENCRYPTION, true, ['encrypt', 'decrypt']);
  } catch {
    return null;
  }
};

export const encryptValue = async (text: string, key: CryptoKey): Promise<string> => {
  const iv = window.crypto.getRandomValues(new Uint8Array(12));
  const encryptedBuf = await window.crypto.subtle.encrypt({ name: ALGO_ENCRYPTION, iv }, key, encode(text));
  return `${bufferToBase64(iv)}.${bufferToBase64(encryptedBuf)}`;
};

export const decryptValue = async (encryptedStr: string | null | undefined, key: CryptoKey): Promise<string | null> => {
  try {
    if (!encryptedStr || typeof encryptedStr !== 'string') return null;
    const sanitized = encryptedStr.replace(/["\s]/g, '');
    if (!sanitized.includes('.')) return null;

    const [ivBase64, ciphertextBase64] = sanitized.split('.');
    const decryptedBuf = await window.crypto.subtle.decrypt(
      { name: ALGO_ENCRYPTION, iv: base64ToBuffer(ivBase64) },
      key,
      base64ToBuffer(ciphertextBase64)
    );
    return decode(decryptedBuf);
  } catch (e) {
    return null;
  }
};

export const generateSalt = () => {
  const bytes = window.crypto.getRandomValues(new Uint8Array(16));
  return bufferToBase64(bytes);
};

export const generateRecoveryKey = () => {
  const charset = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  let ret = "";
  const values = window.crypto.getRandomValues(new Uint8Array(24));
  for(let i=0; i<24; i++) {
    if (i > 0 && i % 6 === 0) ret += "-";
    ret += charset[values[i] % charset.length];
  }
  return ret;
};

/**
 * Exporta a chave mascarando-a com uma assinatura de ambiente.
 * Isso garante que o valor no sessionStorage mude se o ambiente mudar.
 */
export const exportKeyToSession = async (key: CryptoKey): Promise<string> => {
  const raw = await window.crypto.subtle.exportKey('raw', key);
  const bytes = new Uint8Array(raw);
  const fingerprint = getEnvironmentFingerprint();
  const mask = encode(fingerprint);
  
  // XOR Masking
  const masked = new Uint8Array(bytes.length);
  for (let i = 0; i < bytes.length; i++) {
    masked[i] = bytes[i] ^ mask[i % mask.length];
  }
  
  return bufferToBase64(masked.buffer);
};

/**
 * Importa a chave removendo a máscara de ambiente.
 */
export const importKeyFromSession = async (base64Key: string): Promise<CryptoKey> => {
  const maskedBuffer = base64ToBuffer(base64Key);
  const maskedBytes = new Uint8Array(maskedBuffer);
  const fingerprint = getEnvironmentFingerprint();
  const mask = encode(fingerprint);
  
  const raw = new Uint8Array(maskedBytes.length);
  for (let i = 0; i < maskedBytes.length; i++) {
    raw[i] = maskedBytes[i] ^ mask[i % mask.length];
  }
  
  return window.crypto.subtle.importKey('raw', raw.buffer, ALGO_ENCRYPTION, true, ['encrypt', 'decrypt']);
};

export const RECOVERY_SALT = RECOVERY_STATIC_SALT;
